import { queryOptions, useQuery } from '@tanstack/react-query';
import { type SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api, errorMessage, onReauthRequired, setCsrfToken } from './api';
import type { Me } from './types';

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async () => {
    const me = await api<Me>('/me');
    setCsrfToken(me.csrfToken);
    return me;
  },
  staleTime: 5 * 60_000,
  retry: false,
});

/** Текущий сотрудник. Страницы внутри раздела с входом всегда его имеют (проверка в маршруте). */
export function useMe(): Me {
  const { data } = useQuery(meQuery);
  if (!data) throw new Error('useMe() outside of an authenticated route');
  return data;
}

export function can(me: Me, permission: string): boolean {
  return me.permissions.includes(permission);
}

export function useCan(permission: string): boolean {
  return can(useMe(), permission);
}

/**
 * Диалог повторного подтверждения (пароль + код). API-клиент вызывает его, когда сервер
 * отвечает reauth_required, и повторяет запрос после успешного подтверждения.
 */
export function ReauthDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | undefined>(undefined);

  useEffect(() => {
    onReauthRequired(
      () =>
        new Promise<boolean>((resolve) => {
          resolver.current = resolve;
          setPassword('');
          setCode('');
          setError(undefined);
          setOpen(true);
        }),
    );
    return () => {
      onReauthRequired(undefined);
    };
  }, []);

  const finish = (ok: boolean) => {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = undefined;
  };

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api('/auth/reauth', { method: 'POST', body: { password, code }, retried: true });
      finish(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        finish(false);
      }}
      title={t('auth.reauthTitle')}
      description={t('auth.reauthHint')}
    >
      <form onSubmit={(e) => void submit(e)} className="grid gap-3">
        <Field label={t('auth.password')}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('auth.code')}>
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => {
                setCode(e.target.value.trim());
              }}
            />
          )}
        </Field>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              finish(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('common.confirm')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
